import { Injectable } from '@angular/core';
import LogRocket from 'logrocket';
import * as Sentry from '@sentry/angular';
import { BehaviorSubject } from 'rxjs';
import { RoomService } from '../room/room.service';

@Injectable({
    providedIn: 'root',
})
export class UserService {
    constructor(private roomService: RoomService) {}

    /**
     * User details object made up of userName and email
     *  - Set in [updateUserDetails()]{@link UserService#updateUserDetails}
     *  - Subscribed to in [setupUserEvents()]{@link WelcomeComponent#setupUserEvents} in the WelcomeComponent -  to determine whether the user can proceed
     */
    userDetails = new BehaviorSubject({
        userName: '',
        email: '',
    });

    /**
     * roleSelected string
     *  - Set in [getRoomToken()]{@link WelcomeComponent#getRoomToken} in the WelcomeComponent
     *  - Subscribed to in [setupUserEvents()]{@link WelcomeComponent#setupUserEvents} in the WelcomeComponent
     */
    readonly roleSelected = new BehaviorSubject('');

    /**
     * Gets the userName from the [userDetails]{@link UserService#userName} object
     *  - Used in [activeMessages]{@link ChatService#activeMessages} in the ChatService to filter messages based on the active chat state
     *  - Used in [addChatMessage]{@link ChatService#addChatMessage} in the ChatService to add the userName to the message object
     *  - Used to set the [userName]{@link StreamProviderService#userName} in the StreamProviderService
     *  - Used in [ngOnInit]{@link ChatComponent#ngOnInit} in the ChatComponent
     *  - Used to set the [userName]{@link SetupComponent#userName} in the SetupComponent
     *  - Used to set [canProceed]{@link WelcomeComponent#canProceed} in the WelcomeComponent
     */
    get userName(): string {
        return this.userDetails.value.userName;
    }

    /**
     * Gets the email from the [userDetails]{@link @UserService#userDetails} object
     *  - Does not appear to be used
     */
    get email(): string {
        return this.userDetails.value.email;
    }

    /**
     * Gets the role from the [roleSelected]{@link UserService#roleSelected} object
     *  - Used to set the [isModerator]{@link UserService#isModerator}, [isObserver]{@link UserService#isObserver}, and [isParticipant]{@link UserService#isParticipant} booleans
     *  - Used to set the LogRocket userLevel in the [updateUserDetails]{@link UserService#updateUserDetails} function
     */
    get role(): string {
        return this.roleSelected.value;
    }

    /**
     * Boolean to determine if the user is a moderator
     *  - Used in [userConfig]{@link StreamProviderService#userConfig} in the StreamProviderService
     *  - Used in [getCallObject]{@link StreamProviderService#getCallObject} in the StreamProviderService
     *  - Used in [setupPreview]{@link StreamProviderService#setupPreview} in the StreamProviderService
     *  - Used to set the [isModerator]{@link ChatComponent#isModerator} getter in the ChatComponent
     *  - Used to set the [isModerator]{@link RoomComponent#isModerator} getter in the RoomComponent
     * - Used to set the [isModerator]{@link RoomControlsComponent#isModerator} getter in the RoomControlsComponent
     *  - Used to set the [isModerator]{@link SetupComponent#isModerator} getter in the SetupComponent
     *  - Used to set the [showUserDetailsForm]{@link WelcomeComponent#showUserDetailsForm} getter in the WelcomeComponent
     */
    get isModerator(): boolean {
        return this.role === 'moderator';
    }

    /**
     * Boolean to determine if the user is an observer
     *  - Used in [userConfig]{@link StreamProviderService#userConfig} in the StreamProviderService
     *  - Used in [setupPreview]{@link StreamProviderService#setupPreview} in the StreamProviderService
     *  - Used to set the [isObserver]{@link ChatComponent#isObserver} getter in the ChatComponent
     * - Used to set the [isObserver]{@link RoomComponent#isObserver} getter in the RoomComponent
     * - Used to set the [isObserver]{@link RoomControlsComponent#isObserver} getter in the RoomControlsComponent
     * - Used to set the [isObserver]{@link WelcomeComponent#isObserver} getter in the WelcomeComponent
     */
    get isObserver(): boolean {
        return this.role === 'observer' || this.role === 'ai-observer';
    }

    /**
     * Boolean to determine if the user is a participant
     *  - Used to set the [isParticipant]{@link RoomControlsComponent#isParticipant} getter in the RoomControlsComponent
     *  - Used to set the [showUserDetailsForm]{@link WelcomeComponent#showUserDetailsForm} getter in the WelcomeComponent
     */
    get isParticipant(): boolean {
        return this.role === 'participant';
    }

    get isAiModerator(): boolean {
        return this.role === 'ai-moderator';
    }

    get isAiParticipant(): boolean {
        return this.role === 'ai-participant';
    }

    _additionaldata: string = '';

    get additionalData(): string {
        return this._additionaldata;
    }

    set additionalData(value: string) {
        this._additionaldata = value;
    }

    /**
     *  - Sets the [userDetails]{@link UserService#userDetails} subject with the userDetails passed in from the forms
     *  - Sets the Sentry user details
     *  - Sets the LogRocket user details
     *  - Called in [joinRoom()]{@link ObserverDetailsFormComponent#joinRoom} in the ObserverDetailsFormComponent
     *  - Called in [submit()]{@link UserDetailsFormComponent#submit} in the UserDetailsFormComponent
     * @param userDetails - User's details object
     */
    updateUserDetails(userDetails: any) {
        this.userDetails.next(userDetails);

        Sentry.setUser({
            username: userDetails.userName,
            email: userDetails.email,
            roomName: this.roomService.description,
            roomId: this.roomService.roomId,
            userLevel: this.role,
        });

        LogRocket.identify(userDetails.userName, {
            username: userDetails.userName,
            email: userDetails.email,
            roomName: this.roomService.description,
            roomId: this.roomService.roomId,
            userLevel: this.role,
        });
    }

    /**
     * Sets the roleSelected subject with the role passed in
     *  - Called in [getRoomToken()]{@link TaskSetupComponent#getRoomToken()} in the TaskSetupComponent
     *  - Called in [getRoomToken()]{@link WelcomeComponent#getRoomToken()} in the WelcomeComponent
     * @param {string} value
     */
    updateRoleSelected(value: string) {
        this.roleSelected.next(value);
    }

    dailyNetworkStatsToLogrocket(networkStats: any) {
        if (!networkStats) {
            return;
        }

        try {
            const safeTrack = (name: string, value: any) => {
                if (value !== null && value !== undefined) {
                    LogRocket.track(name, value);
                }
            };

            safeTrack('network-stats-threshold', networkStats?.threshold);
            safeTrack('network-stats-quality', networkStats?.quality);
            safeTrack('network-stats-networkState', networkStats?.networkState);
            safeTrack(
                'network-stats-networkStateReasons',
                networkStats?.networkStateReasons,
            );
        } catch (error) {
            console.error('Error logging network stats to LogRocket:', error);
        }
    }
}
